/**
 * Social share targets. Ported from the .in engine's @common/utils/sharers.js.
 *
 * One deliberate change: .in's Facebook sharer posts through
 * `dialog/share?app_id=612463668776658`, which is GrabOn **India's** Facebook
 * app. Sharing International pages through it would attribute every share to
 * the wrong property, and the app's allowed-domain list does not include these
 * hosts, so the dialog can refuse the URL outright. `sharer.php` needs no app
 * id and works for any domain — swap it back only when International has its
 * own app id to put here.
 */

/** .in's popup geometry — small, chromeless, centred by the browser. */
export function openShareWindow(url) {
  return window.open(
    url,
    null,
    'width=600, height=400, location=0, menubar=0, resizeable=0, scrollbars=0, status=0, titlebar=0, toolbar=0',
  );
}

const enc = encodeURIComponent;

export function shareFacebook(shareUrl) {
  return openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`);
}

export function shareTwitter(shareUrl, shareText, hashtags) {
  return openShareWindow(
    `https://twitter.com/share?url=${enc(shareUrl)}&text=${enc(shareText)}&hashtags=${enc(hashtags || '')}`,
  );
}

export function shareLinkedIn(shareUrl) {
  // LinkedIn dropped support for a supplied title/summary years ago; it reads
  // the page's own og: tags. .in passes shareText and it is ignored there too.
  return openShareWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`);
}

export function shareWhatsApp(shareUrl, shareText) {
  return openShareWindow(`https://api.whatsapp.com/send?text=${enc(`${shareText}\n${shareUrl}`)}`);
}

/**
 * Instagram has no web share endpoint — there is nothing to post to. .in opens
 * `instagram.com/?url=…`, which lands on the feed and drops the link. Kept
 * because the icon is in the design and .in ships it, but it is a link to
 * Instagram, not a share.
 */
export function shareInstagram(shareUrl) {
  return openShareWindow(`https://www.instagram.com/?url=${enc(shareUrl)}`);
}

/**
 * Share by email. No popup geometry — `mailto:` hands off to the mail client,
 * and a 600x400 chrome-less window for that is a blank tab on most systems.
 */
export function shareEmail(shareUrl, shareSubject, shareBody) {
  return window.open(
    `mailto:?subject=${enc(shareSubject || '')}&body=${enc(`${shareBody || ''}\n${shareUrl}`)}`,
  );
}

/**
 * Copy a URL to the clipboard.
 *
 * .in returns 200/404 out of a `.then`, which returns from the callback rather
 * than the function and is always undefined at the call site. This resolves to
 * a boolean instead, so callers can actually act on it.
 */
export function copyUrlToClipboard(shareUrl) {
  const url = shareUrl == null ? window.location.href : shareUrl;
  if (!navigator.clipboard || !navigator.clipboard.writeText) return Promise.resolve(false);
  return navigator.clipboard.writeText(url).then(
    () => true,
    () => false,
  );
}
